import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Injector, Injectable } from '@angular/core';
import { RollbarService } from './rollbar';
import { AuthService } from '../services/auth/auth.service';
import { environment } from 'src/environments/environment';

const MAX_DETAIL_LENGTH = 500;

function isOurApi(url: string): boolean {
  return url.startsWith(environment.api_root);
}

/* Completing a login. A 401 here is a login that failed, not a session that ran out. */
const LOGIN_CALLBACK = 'headless/auth/callback';

/* Failures that are part of normal use and not worth reporting: an expired or missing session */
function isExpected(error: HttpErrorResponse, url: string): boolean {
  return error.status === 401 && !url.includes(LOGIN_CALLBACK);
}

/* The request's URL without the parts that vary between occurrences, so that Rollbar groups them */
function describeUrl(url: string): string {
  let path = (isOurApi(url) ? url.slice(environment.api_root.length) : url).split('?')[0];
  try {
    path = decodeURI(path);
  } catch {
    // Not valid percent-encoding; describe it as it came
  }
  return path
    .replace(/\/\d+(?=\/|$)/g, '/:id')
    // An AI chat is known by a UUID, or by an older id that is not all digits either
    .replace(/(ai_api\/threads)\/[^/]+/, '$1/:id');
}

function describeBody(body: unknown): string {
  if (body === null || body === undefined) {
    return '';
  }
  const text = typeof body === 'string' ? body : JSON.stringify(body);
  return text.slice(0, MAX_DETAIL_LENGTH);
}

/* Report failed HTTP requests to Rollbar, and send the user to log in again when their session is rejected */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private injector: Injector, private authService: AuthService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const isAuthEndpoint = request.url.includes('headless/auth/');
        if (error.status === 401 && !isAuthEndpoint) {
          this.authService.sessionEnded();
        }
        this.report(request, error);
        return throwError(error);
      })
    );
  }

  private report(request: HttpRequest<any>, error: HttpErrorResponse): void {
    const url = error.url || request.urlWithParams;
    if (isExpected(error, url)) {
      return;
    }

    const rollbar = this.injector.get(RollbarService);
    const noResponse = error.status === 0;
    const what = `${request.method} ${describeUrl(url)}`;
    const message = noResponse ? `No response: ${what}` : `HTTP ${error.status} ${what}`;
    const extra = {
      status: error.status,
      statusText: error.statusText,
      method: request.method,
      url,
      detail: describeBody(error.error),
    };

    // A request that never got a response, or one to someone else's server
    // (a news feed), is not a fault in GPS
    if (noResponse || !isOurApi(url)) {
      rollbar.warn(message, extra);
    } else {
      rollbar.error(message, extra);
    }
  }
}
